import type { AdminSession } from '@/lib/admin-auth';
import { resolveMediaUrl } from '@/services/api-client';
import { findMockUserByUsername } from '@/lib/painel-rbac';

export type PasswordStrength = 'very_weak' | 'weak' | 'good' | 'strong' | 'excellent';

export const PASSWORD_STRENGTH_LABELS: Record<PasswordStrength, string> = {
  very_weak: 'Muito fraca',
  weak: 'Fraca',
  good: 'Boa',
  strong: 'Forte',
  excellent: 'Excelente',
};

export const PASSWORD_STRENGTH_COLORS: Record<PasswordStrength, string> = {
  very_weak: 'bg-rose-500',
  weak: 'bg-orange-500',
  good: 'bg-amber-400',
  strong: 'bg-sky-400',
  excellent: 'bg-emerald-400',
};

export const PASSWORD_STRENGTH_WIDTH: Record<PasswordStrength, string> = {
  very_weak: 'w-1/5',
  weak: 'w-2/5',
  good: 'w-3/5',
  strong: 'w-4/5',
  excellent: 'w-full',
};

export type AccountPreferences = {
  panelNotifications: boolean;
  commentAlerts: boolean;
  emailNotifications: boolean;
  profileVisible: boolean;
};

export type AccountSettingsForm = {
  avatarPreview: string | null;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  bio: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  preferences: AccountPreferences;
};

export type AccountSessionView = {
  id: string;
  os: string;
  browser: string;
  location: string;
  lastAccessLabel: string;
  isCurrent: boolean;
};

export const BIO_MAX_LENGTH = 250;

export const AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

const DEFAULT_PREFERENCES: AccountPreferences = {
  panelNotifications: true,
  commentAlerts: true,
  emailNotifications: false,
  profileVisible: true,
};

function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? '', lastName: '' };
  }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/** Monta o estado inicial do formulário a partir da sessão mock atual. */
export function buildAccountSettingsFromSession(session: AdminSession): AccountSettingsForm {
  const mockUser = findMockUserByUsername(session.username);
  const { firstName, lastName } = splitDisplayName(session.name);

  return {
    avatarPreview: resolveMediaUrl(session.avatarUrl),
    firstName,
    lastName,
    username: session.username,
    email: session.email,
    bio: mockUser ? '' : '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    preferences: { ...DEFAULT_PREFERENCES },
  };
}

export function formatSessionLastAccess(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Hoje às ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();
  if (isYesterday) return `Ontem às ${time}`;

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) return 'very_weak';

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (password.length < 6) return 'very_weak';
  if (password.length < 8) return 'weak';
  if (score <= 2) return 'weak';
  if (score === 3) return 'good';
  if (score === 4) return 'strong';
  return 'excellent';
}

export function validatePasswordChange(input: {
  newPassword: string;
  confirmPassword: string;
}): string | null {
  if (!input.newPassword && !input.confirmPassword) return null;
  if (input.newPassword.length < 8) {
    return 'A nova senha deve ter pelo menos 8 caracteres.';
  }
  if (input.newPassword !== input.confirmPassword) {
    return 'A confirmação não coincide com a nova senha.';
  }
  return null;
}

/** Simula persistência futura — apenas delay visual. */
export function simulateAccountSettingsSave(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 900);
  });
}
